import type { MutableRefObject, RefObject } from "react";
import { useCallback, useEffect } from "react";
import {
  getVisibleColumnRange,
  scrollToColumnIndex,
} from "./kanban-scroll-helpers";
import type { SetColumnRefInput, VisibleColumnRange } from "./kanban-scroll-types";

interface UseKanbanColumnNavigationInput {
  clearHoveredCard: () => void;
  columnCount: number;
  columnRefsRef: MutableRefObject<Array<HTMLDivElement | null>>;
  scrollContainerRef: RefObject<HTMLDivElement | null>;
}

interface KanbanColumnNavigation {
  handleScrollToNextColumn: () => void;
  handleScrollToPrevColumn: () => void;
  setColumnRef: (input: SetColumnRefInput) => void;
}

export function useKanbanColumnNavigation({
  clearHoveredCard,
  columnCount,
  columnRefsRef,
  scrollContainerRef,
}: UseKanbanColumnNavigationInput): KanbanColumnNavigation {
  const setColumnRef = useCallback((refInput: SetColumnRefInput): void => {
    columnRefsRef.current[refInput.columnIndex] = refInput.columnNode;
  }, [columnRefsRef]);

  const getCurrentVisibleRange = useCallback((): VisibleColumnRange | null => {
    const scrollContainerNode = scrollContainerRef.current;
    if (!scrollContainerNode) {
      return null;
    }

    const columnNodes = columnRefsRef.current.filter(
      (columnNode): columnNode is HTMLDivElement => columnNode != null,
    );
    if (columnNodes.length === 0) {
      return null;
    }

    return getVisibleColumnRange({ columnNodes, scrollContainerNode });
  }, [columnRefsRef, scrollContainerRef]);

  const scrollToCurrentColumnIndex = useCallback((targetColumnIndex: number): void => {
    scrollToColumnIndex({
      scrollContainerNode: scrollContainerRef.current,
      columnNode: columnRefsRef.current[targetColumnIndex],
    });
  }, [columnRefsRef, scrollContainerRef]);

  const handleScrollToPrevColumn = useCallback((): void => {
    clearHoveredCard();
    const visibleRange = getCurrentVisibleRange();
    if (!visibleRange) {
      return;
    }

    const targetColumnIndex = Math.max(0, visibleRange.first - 1);
    if (targetColumnIndex === visibleRange.first) {
      return;
    }

    scrollToCurrentColumnIndex(targetColumnIndex);
  }, [clearHoveredCard, getCurrentVisibleRange, scrollToCurrentColumnIndex]);

  const handleScrollToNextColumn = useCallback((): void => {
    clearHoveredCard();
    const visibleRange = getCurrentVisibleRange();
    if (!visibleRange) {
      return;
    }

    const maxColumnIndex = columnRefsRef.current.length - 1;
    if (maxColumnIndex < 0) {
      return;
    }

    const targetColumnIndex = Math.min(maxColumnIndex, visibleRange.last + 1);
    if (targetColumnIndex === visibleRange.last) {
      return;
    }

    scrollToCurrentColumnIndex(targetColumnIndex);
  }, [clearHoveredCard, columnRefsRef, getCurrentVisibleRange, scrollToCurrentColumnIndex]);

  useEffect(() => {
    columnRefsRef.current = columnRefsRef.current.slice(0, columnCount);
  }, [columnCount, columnRefsRef]);

  return {
    handleScrollToNextColumn,
    handleScrollToPrevColumn,
    setColumnRef,
  };
}
